import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuthStore } from '../../stores';
const emptyForm = (defaultRole = 'viewer') => ({
    email: '',
    password: '',
    name: '',
    surname: '',
    role: defaultRole,
});
function generatePassword(length = 16) {
    const upper = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
    const lower = 'abcdefghijkmnopqrstuvwxyz';
    const digits = '23456789';
    const symbols = '!@#$%&*?';
    const all = upper + lower + digits + symbols;
    const pick = (chars) => chars[Math.floor(Math.random() * chars.length)];
    const required = [pick(upper), pick(lower), pick(digits), pick(symbols)];
    const bytes = new Uint32Array(Math.max(length - required.length, 0));
    crypto.getRandomValues(bytes);
    const rest = Array.from(bytes, (n) => all[n % all.length]);
    const chars = [...required, ...rest];
    for (let i = chars.length - 1; i > 0; i -= 1) {
        const j = Math.floor(Math.random() * (i + 1));
        [chars[i], chars[j]] = [chars[j], chars[i]];
    }
    return chars.join('');
}
export function Users() {
    const { accessToken, user: currentUser } = useAuthStore();
    const [users, setUsers] = useState([]);
    const [roles, setRoles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [actionLoading, setActionLoading] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [formData, setFormData] = useState(emptyForm());
    const [formError, setFormError] = useState('');
    const [saving, setSaving] = useState(false);
    const [generatedHint, setGeneratedHint] = useState(false);
    const fetchUsers = useCallback(async () => {
        try {
            const response = await fetch('/api/admin/users', {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
            });
            if (!response.ok) {
                throw new Error('Failed to fetch users');
            }
            const data = await response.json();
            setUsers(data);
        }
        catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load users');
        }
    }, [accessToken]);
    const fetchRoles = useCallback(async () => {
        try {
            const response = await fetch('/api/admin/roles', {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
            });
            if (!response.ok) {
                throw new Error('Failed to fetch roles');
            }
            const data = await response.json();
            setRoles(data);
        }
        catch (err) {
            console.error('Failed to fetch roles:', err);
        }
    }, [accessToken]);
    useEffect(() => {
        const loadData = async () => {
            setLoading(true);
            await Promise.all([fetchUsers(), fetchRoles()]);
            setLoading(false);
        };
        loadData();
    }, [fetchUsers, fetchRoles]);
    const handleOpenModal = () => {
        const defaultRole = roles.includes('viewer')
            ? 'viewer'
            : roles[0] ?? 'viewer';
        setFormData(emptyForm(defaultRole));
        setFormError('');
        setGeneratedHint(false);
        setShowModal(true);
    };
    const handleCloseModal = () => {
        if (saving)
            return;
        setShowModal(false);
        setFormError('');
        setGeneratedHint(false);
    };
    const handleGeneratePassword = async () => {
        const password = generatePassword(16);
        setFormData((prev) => ({ ...prev, password }));
        setGeneratedHint(true);
        try {
            await navigator.clipboard.writeText(password);
        }
        catch {
            // Clipboard may be denied; password still fills the field.
        }
    };
    const handleCreateUser = async (e) => {
        e.preventDefault();
        setFormError('');
        setSaving(true);
        setError('');
        try {
            const response = await fetch('/api/admin/users', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${accessToken}`,
                },
                body: JSON.stringify({
                    email: formData.email.trim(),
                    password: formData.password,
                    name: formData.name.trim(),
                    surname: formData.surname.trim(),
                    role: formData.role,
                }),
            });
            const data = (await response.json().catch(() => ({})));
            if (!response.ok) {
                throw new Error(data.message || 'Kullanıcı oluşturulamadı');
            }
            if (data.id && data.email) {
                setUsers((prev) => [
                    {
                        id: data.id,
                        email: data.email,
                        name: data.name ?? '',
                        surname: data.surname ?? '',
                        fullName: data.fullName || `${data.name ?? ''} ${data.surname ?? ''}`.trim() || data.email,
                        role: data.role || formData.role,
                    },
                    ...prev,
                ]);
            }
            else {
                await fetchUsers();
            }
            setShowModal(false);
            setGeneratedHint(false);
        }
        catch (err) {
            setFormError(err instanceof Error ? err.message : 'Kullanıcı oluşturulamadı');
        }
        finally {
            setSaving(false);
        }
    };
    const handleRoleChange = async (userId, newRole) => {
        setActionLoading(userId);
        setError('');
        try {
            const response = await fetch(`/api/admin/users/${userId}/role`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${accessToken}`,
                },
                body: JSON.stringify({ role: newRole }),
            });
            if (!response.ok) {
                const data = await response.json();
                throw new Error(data.message || 'Failed to update role');
            }
            setUsers((prev) => prev.map((u) => (u.id === userId ? { ...u, role: newRole } : u)));
        }
        catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to update role');
        }
        finally {
            setActionLoading(null);
        }
    };
    const handleDeleteUser = async (userId, userName) => {
        if (!confirm(`"${userName}" kullanıcısını silmek istediğinize emin misiniz?`)) {
            return;
        }
        setActionLoading(userId);
        setError('');
        try {
            const response = await fetch(`/api/admin/users/${userId}`, {
                method: 'DELETE',
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
            });
            if (!response.ok) {
                const data = await response.json();
                throw new Error(data.message || 'Failed to delete user');
            }
            setUsers((prev) => prev.filter((u) => u.id !== userId));
        }
        catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to delete user');
        }
        finally {
            setActionLoading(null);
        }
    };
    const getRoleBadgeClass = (role) => {
        switch (role) {
            case 'system':
                return 'role-badge role-system';
            case 'admin':
                return 'role-badge role-admin';
            case 'editor':
                return 'role-badge role-editor';
            default:
                return 'role-badge role-viewer';
        }
    };
    if (loading) {
        return _jsx("div", { className: "admin-loading", children: "Y\u00FCkleniyor..." });
    }
    return (_jsxs("div", { className: "admin-page", children: [_jsxs("header", { className: "admin-page-header", children: [_jsxs("div", { children: [_jsx("h1", { children: "Users" }), _jsx("p", { children: "Kullan\u0131c\u0131 hesaplar\u0131n\u0131 ve rollerini y\u00F6netin" })] }), _jsx("button", { type: "button", className: "admin-btn admin-btn-primary", onClick: handleOpenModal, children: "+ Yeni Kullan\u0131c\u0131" })] }), error && _jsx("div", { className: "admin-error", children: error }), _jsxs("section", { className: "admin-table-section", children: [_jsx("div", { className: "admin-table-toolbar", children: _jsxs("h2", { children: ["Kullan\u0131c\u0131lar (", users.length, ")"] }) }), _jsx("div", { className: "admin-table-container", children: _jsxs("table", { className: "admin-table", children: [_jsx("thead", { children: _jsxs("tr", { children: [_jsx("th", { children: "User" }), _jsx("th", { children: "Email" }), _jsx("th", { children: "Role" }), _jsx("th", { children: "Actions" })] }) }), _jsx("tbody", { children: users.map((user) => (_jsxs("tr", { children: [_jsx("td", { children: _jsxs(Link, { to: `/admin/users/${user.id}`, className: "user-info user-info-link", children: [_jsx("div", { className: "user-avatar", children: (user.name || user.email).charAt(0).toUpperCase() }), _jsxs("div", { className: "user-details", children: [_jsx("span", { className: "user-name", children: user.fullName || user.email }), user.id === currentUser?.id && (_jsx("span", { className: "user-you-badge", children: "You" }))] })] }) }), _jsx("td", { children: _jsx("span", { className: "user-email", children: user.email }) }), _jsx("td", { children: _jsx("select", { className: getRoleBadgeClass(user.role), value: user.role, onChange: (e) => handleRoleChange(user.id, e.target.value), disabled: actionLoading === user.id || user.id === currentUser?.id, "aria-label": `Role for ${user.fullName}`, children: roles.map((role) => (_jsx("option", { value: role, children: role }, role))) }) }), _jsx("td", { children: _jsxs("div", { className: "admin-row-actions", children: [_jsx(Link, { to: `/admin/users/${user.id}`, className: "admin-btn admin-btn-secondary admin-btn-tiny", children: "Detay" }), _jsx("button", { className: "admin-delete-btn", onClick: () => handleDeleteUser(user.id, user.fullName || user.email), disabled: actionLoading === user.id || user.id === currentUser?.id, title: user.id === currentUser?.id ? 'You cannot delete yourself' : 'Delete user', children: actionLoading === user.id ? '...' : '🗑' })] }) })] }, user.id))) })] }) })] }), showModal && (_jsx("div", { className: "admin-modal-overlay", onClick: handleCloseModal, children: _jsxs("div", { className: "admin-modal-content", onClick: (e) => e.stopPropagation(), children: [_jsxs("div", { className: "admin-modal-header", children: [_jsx("h2", { children: "Yeni Kullan\u0131c\u0131" }), _jsx("button", { type: "button", className: "admin-modal-close", onClick: handleCloseModal, children: "\u2715" })] }), _jsxs("form", { onSubmit: handleCreateUser, className: "admin-form", children: [formError && _jsx("div", { className: "admin-error", children: formError }), _jsxs("div", { className: "admin-form-row", children: [_jsx("div", { className: "admin-form-group", children: _jsxs("label", { className: "admin-form-label", children: ["Ad", _jsx("input", { className: "admin-form-input", value: formData.name, onChange: (e) => setFormData({ ...formData, name: e.target.value }), placeholder: "Ada", autoComplete: "off" })] }) }), _jsx("div", { className: "admin-form-group", children: _jsxs("label", { className: "admin-form-label", children: ["Soyad", _jsx("input", { className: "admin-form-input", value: formData.surname, onChange: (e) => setFormData({ ...formData, surname: e.target.value }), placeholder: "Lovelace", autoComplete: "off" })] }) })] }), _jsx("div", { className: "admin-form-group", children: _jsxs("label", { className: "admin-form-label", children: ["Email *", _jsx("input", { type: "email", className: "admin-form-input", value: formData.email, onChange: (e) => setFormData({ ...formData, email: e.target.value }), required: true, placeholder: "user@example.com", autoComplete: "off" })] }) }), _jsxs("div", { className: "admin-form-group", children: [_jsxs("label", { className: "admin-form-label", children: ["\u015Eifre *", _jsxs("div", { className: "admin-password-field", children: [_jsx("input", { type: "text", className: "admin-form-input", value: formData.password, onChange: (e) => {
                                                                setFormData({ ...formData, password: e.target.value });
                                                                setGeneratedHint(false);
                                                            }, required: true, minLength: 6, placeholder: "En az 6 karakter", autoComplete: "new-password" }), _jsx("button", { type: "button", className: "admin-btn admin-btn-secondary", onClick: handleGeneratePassword, children: "\u00DCret" })] })] }), generatedHint ? (_jsx("p", { className: "admin-form-hint", children: "\u015Eifre \u00FCretildi ve panoya kopyaland\u0131. Kullan\u0131c\u0131ya iletmeyi unutmay\u0131n." })) : null] }), _jsx("div", { className: "admin-form-group", children: _jsxs("label", { className: "admin-form-label", children: ["Rol", _jsx("select", { className: "admin-form-select", value: formData.role, onChange: (e) => setFormData({ ...formData, role: e.target.value }), children: roles.map((role) => (_jsx("option", { value: role, children: role }, role))) })] }) }), _jsxs("div", { className: "admin-form-actions", children: [_jsx("button", { type: "button", className: "admin-btn admin-btn-secondary", onClick: handleCloseModal, disabled: saving, children: "\u0130ptal" }), _jsx("button", { type: "submit", className: "admin-btn admin-btn-primary", disabled: saving, children: saving ? 'Kaydediliyor…' : 'Kullanıcı Oluştur' })] })] })] }) }))] }));
}
